import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import logo from "@/assets/fh-systems-logo-new.png";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const {
    user,
    isAdmin,
    isModerator,
    signOut,
  } = useAuth();

  // Base navigation links
  const baseLinks = [{
    label: "Home",
    path: "/"
  }, {
    label: "Services",
    path: "/services"
  }, {
    label: "How It Works",
    path: "/how-it-works"
  }, {
    label: "FAQ",
    path: "/faq"
  }, {
    label: "About",
    path: "/about"
  }, {
    label: "Pricing",
    path: "/pricing"
  }, {
    label: "Contact",
    path: "/contact"
  }];

  // Dynamic auth-related links based on user status
  const authLinks = user ? isAdmin || isModerator ? [{
    label: "Admin",
    path: "/admin"
  }] : [{
    label: "Dashboard",
    path: "/dashboard"
  }] : [{
    label: "Referral",
    path: "/referral"
  }, {
    label: "Sign In",
    path: "/auth"
  }];
  
  const navLinks = [...baseLinks, ...authLinks];
  const isActive = (path: string) => location.pathname === path;

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 border-b border-neon-glow/10"
      style={{
        // Use solid background on mobile instead of backdrop-blur
        backgroundColor: isMobile ? "hsl(210, 100%, 8%)" : "hsl(210, 100%, 8%, 0.8)",
        backdropFilter: isMobile ? "none" : "blur(12px)",
        WebkitBackdropFilter: isMobile ? "none" : "blur(12px)",
      }}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <img alt="F&H Systems" width={48} height={48} className="h-10 md:h-12 w-auto" src="/lovable-uploads/5764043f-503d-4405-9085-0996151b8b45.png" />
          </Link>

          {/* Desktop Navigation - Horizontal */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map(link => (
              <Link 
                key={link.path} 
                to={link.path} 
                className={`
                  text-sm font-medium transition-colors duration-200 
                  relative py-1
                  after:content-[''] after:absolute after:w-full after:scale-x-0 
                  after:h-0.5 after:bottom-0 after:left-0 
                  after:bg-gradient-to-r after:from-neon-glow after:to-radiant-blue
                  after:origin-bottom-right after:transition-transform after:duration-300 
                  hover:after:scale-x-100 hover:after:origin-bottom-left
                  ${isActive(link.path) ? "text-neon-glow after:scale-x-100" : "text-white hover:text-neon-glow"}
                `}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA & Language Toggle */}
          <div className="hidden lg:flex items-center space-x-4">
            {user ? (
              <Button onClick={signOut} variant="outline" className="border-neon-glow/30 hover:bg-neon-glow/10">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            ) : (
              <Link to="/contact">
                <Button className="bg-accent hover:bg-accent/90 text-accent-foreground">
                  Get Started
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile/Tablet Menu Button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button className="p-2 hover:bg-secondary rounded-md transition-colors" aria-label="Open navigation menu">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent 
              side="right" 
              className="w-[280px] sm:w-80 border-l border-neon-glow/20 overflow-y-auto"
              style={{
                backgroundColor: "hsl(210, 100%, 8%)",
              }}
            >
              <div className="flex flex-col h-full pt-8">
                {/* Logo in Sidebar */}
                <div className="mb-8 px-2">
                  <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
                    <img src={logo} alt="F&H Systems" width={40} height={40} className="h-10 w-auto" />
                  </Link>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 flex flex-col space-y-2">
                  {navLinks.map(link => (
                    <Link 
                      key={link.path} 
                      to={link.path} 
                      onClick={() => setIsOpen(false)} 
                      className={`
                        text-base font-medium px-4 py-3 rounded-lg 
                        transition-colors duration-200
                        relative overflow-hidden
                        before:content-[''] before:absolute before:left-0 before:top-0 
                        before:h-full before:w-1 before:bg-gradient-to-b 
                        before:from-neon-glow before:to-radiant-blue
                        before:scale-y-0 before:origin-top
                        before:transition-transform before:duration-300
                        hover:before:scale-y-100
                        ${isActive(link.path) ? "bg-neon-glow/20 text-neon-glow before:scale-y-100 pl-6" : "text-white hover:bg-deep-navy/50 hover:text-neon-glow hover:pl-6"}
                      `}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>

                {/* Bottom Actions */}
                <div className="flex flex-col space-y-3 px-2 pt-4 border-t border-neon-glow/20">
                  {user ? (
                    <Button 
                      onClick={() => {
                        setIsOpen(false);
                        signOut();
                      }} 
                      variant="outline" 
                      className="w-full border-neon-glow/30 hover:bg-neon-glow/10"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </Button>
                  ) : (
                    <Link to="/contact" onClick={() => setIsOpen(false)}>
                      <Button className="w-full bg-accent hover:bg-accent/90">
                        Get Started
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};